/**
 * Hook for public provider acquisition popup campaigns (visitors only).
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isProviderAcquisitionPopupsEnabledClient } from '../../shared/providerAcquisition/featureFlag';
import { isProviderAcquisitionPathExcluded } from '../../shared/providerAcquisition/pageTargeting';
import {
  getOrCreateProviderVisitorId,
  getProviderAcquisitionSessionId,
  wasProviderCampaignShownThisSession,
  markProviderCampaignShownThisSession,
} from '../utils/providerVisitorId';
import { trackEvent } from '../utils/analytics';
import { logger } from '../utils/logger';

export interface ProviderAcquisitionCampaign {
  id: number;
  popup_type: 'modal' | 'slide_in' | 'banner' | 'fullscreen';
  trigger_type: 'page_load' | 'time_delay' | 'scroll_percentage' | 'exit_intent' | 'idle_time';
  trigger_value?: number;
  content: ProviderAcquisitionContent;
}

export interface ProviderAcquisitionContent {
  id: number;
  campaign_id: number;
  variant_name: string;
  title: string;
  subtitle?: string;
  body_text?: string;
  bullets?: string[];
  primary_cta_text: string;
  secondary_cta_text?: string;
  primary_cta_url: string;
  secondary_cta_url?: string;
  image_url?: string;
  background_color?: string;
  text_color?: string;
}

export type ProviderAcquisitionInteractionAction =
  | 'viewed'
  | 'dismissed'
  | 'clicked_primary'
  | 'clicked_secondary'
  | 'closed';

export function useProviderAcquisitionCampaigns() {
  const { isAuthenticated, isInitializing } = useAuth();
  const location = useLocation();
  const enabled = isProviderAcquisitionPopupsEnabledClient();
  const [activeCampaign, setActiveCampaign] = useState<ProviderAcquisitionCampaign | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useRef(0);
  const pageLoadRef = useRef(Date.now());
  const fetchingRef = useRef(false);

  const recordInteraction = useCallback(
    async (
      action: ProviderAcquisitionInteractionAction,
      campaign: ProviderAcquisitionCampaign,
      metadata?: Record<string, unknown>,
    ) => {
      try {
        const params = new URLSearchParams(window.location.search);
        await axios.post('/api/provider-acquisition/interaction', {
          visitor_id: getOrCreateProviderVisitorId(),
          campaign_id: campaign.id,
          content_id: campaign.content.id,
          action,
          page_url: window.location.pathname + window.location.search,
          utm_source: params.get('utm_source') ?? undefined,
          utm_campaign: params.get('utm_campaign') ?? undefined,
          metadata: {
            session_id: getProviderAcquisitionSessionId(),
            scroll_percentage: scrollRef.current,
            time_on_page: Date.now() - pageLoadRef.current,
            ...metadata,
          },
        });
      } catch (err) {
        logger.error('Provider acquisition interaction failed', err, {});
      }
    },
    [],
  );

  const fetchCampaign = useCallback(async () => {
    if (!enabled || isInitializing || isAuthenticated) {
      setActiveCampaign(null);
      return;
    }

    const pathname = location.pathname;
    if (pathname.startsWith('/embed/') || isProviderAcquisitionPathExcluded(pathname)) {
      setActiveCampaign(null);
      return;
    }

    if (fetchingRef.current) return;
    fetchingRef.current = true;

    try {
      setIsLoading(true);
      const response = await axios.get('/api/provider-acquisition/active', {
        params: {
          page_url: pathname + location.search,
          visitor_id: getOrCreateProviderVisitorId(),
          session_id: getProviderAcquisitionSessionId(),
        },
        timeout: 10000,
      });

      if (response.data.success && response.data.campaign) {
        setActiveCampaign(response.data.campaign as ProviderAcquisitionCampaign);
      } else {
        setActiveCampaign(null);
      }
    } catch (err) {
      setActiveCampaign(null);
      if (axios.isAxiosError(err) && err.response?.status !== 503) {
        logger.error('Provider acquisition fetch failed', err, {});
      }
    } finally {
      setIsLoading(false);
      fetchingRef.current = false;
    }
  }, [enabled, isAuthenticated, isInitializing, location.pathname, location.search]);

  useEffect(() => {
    pageLoadRef.current = Date.now();
    void fetchCampaign();
  }, [fetchCampaign]);

  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = doc.scrollTop || document.body.scrollTop;
      const height = doc.scrollHeight - doc.clientHeight;
      scrollRef.current = height > 0 ? Math.round((scrollTop / height) * 100) : 0;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const showPopup = useCallback(
    async (campaign: ProviderAcquisitionCampaign) => {
      if (wasProviderCampaignShownThisSession(campaign.id)) {
        return;
      }
      markProviderCampaignShownThisSession(campaign.id);
      await recordInteraction('viewed', campaign);
      trackEvent({
        action: 'provider_acquisition_popup_impression',
        category: 'provider_acquisition',
        label: campaign.content.variant_name,
        custom_parameters: {
          campaign_id: campaign.id,
          page_path: location.pathname,
        },
      });
    },
    [location.pathname, recordInteraction],
  );

  const dismissPopup = useCallback(
    async (campaign: ProviderAcquisitionCampaign, action: 'dismissed' | 'closed' = 'dismissed') => {
      await recordInteraction(action, campaign);
      trackEvent({
        action: 'provider_acquisition_popup_dismiss',
        category: 'provider_acquisition',
        label: action,
        custom_parameters: { campaign_id: campaign.id },
      });
      setActiveCampaign(null);
    },
    [recordInteraction],
  );

  const handleCtaClick = useCallback(
    async (campaign: ProviderAcquisitionCampaign, ctaType: 'primary' | 'secondary', url?: string) => {
      const action = ctaType === 'primary' ? 'clicked_primary' : 'clicked_secondary';
      await recordInteraction(action, campaign, { clicked_url: url });
      trackEvent({
        action: 'provider_acquisition_popup_cta_click',
        category: 'provider_acquisition',
        label: ctaType,
        custom_parameters: { campaign_id: campaign.id, destination: url },
      });
      setActiveCampaign(null);
      if (url) {
        if (url.startsWith('http')) {
          window.open(url, '_blank', 'noopener,noreferrer');
        } else {
          window.location.href = url;
        }
      }
    },
    [recordInteraction],
  );

  const isPopupDue = useCallback((campaign: ProviderAcquisitionCampaign): boolean => {
    if (wasProviderCampaignShownThisSession(campaign.id)) {
      return false;
    }
    switch (campaign.trigger_type) {
      case 'page_load':
        return true;
      case 'time_delay':
        return false;
      case 'scroll_percentage':
        return scrollRef.current >= (campaign.trigger_value ?? 50);
      case 'exit_intent':
      case 'idle_time':
        return false;
      default:
        return false;
    }
  }, []);

  return {
    enabled,
    activeCampaign,
    isLoading,
    fetchCampaign,
    showPopup,
    dismissPopup,
    handleCtaClick,
    isPopupDue,
    scrollRef,
    pageLoadRef,
  };
}
